import { Slider } from 'antd';
import SearchBar from 'components/SearchBar/SearchBar';
import { AlertCard } from 'components/WeatherAlerts/AlertCard';
import { usePerms } from 'contexts/PermsContext';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FaSort } from 'react-icons/fa';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GetPageFromList, ProcessAlert } from 'utils/alertUtils';
import PageIndex from '../UI/PageIndex';
import Toggle from '../UI/Toggle';
import AlertWidget from './AlertWidget';

const alertLimit = 100;

export const AlertList = ({
  widget,
  dimensions,
  title,
  alertsList = [],
  setAlertsList,
  sidebarOpen,
  ROUTE,
  preview = false,
}) => {
  const perms = usePerms();
  const navigate = useNavigate();
  const [severitySortDirection, setSeveritySortDirection] = useState(null); // true is ascending, false is descending
  const [pageIndex, setPageIndex] = useState(1);
  const [maxDist, setMaxDist] = useState(100);
  const [showLocalOnly, setShowLocalOnly] = useState(false);
  const [filteredAlertListPaged, setFilteredAlertListPaged] = useState(alertsList);
  const [filteredAlertList, setFilteredAlertList] = useState(alertsList);
  const [params] = useSearchParams();

  const severitySort = () => {
    const newValue = !severitySortDirection;
    setSeveritySortDirection(newValue);
    UpdateFilteredLists({ severitySortValue: newValue });
  };

  useEffect(() => {
    try {
      if (!alertsList[0] || !!alertsList[0].distanceToUser) return;
    } catch (e) {}

    (async () => {
      //Inject needed information into the alert object
      const processedAlerts = [];
      for (let i = 0; i < alertsList.length; i++)
        processedAlerts.push(await ProcessAlert(alertsList[i]));

      setAlertsList(processedAlerts);
      UpdateFilteredLists({ alerts: processedAlerts });
    })();
  }, [alertsList, setAlertsList, filteredAlertList]);

  useEffect(() => {
    UpdateFilteredLists();
  }, [showLocalOnly, maxDist, params, alertsList]);

  const FilterAlertList = () => {
    return alertsList?.filter((alert) => {
      if (showLocalOnly) {
        if (alert.distanceToUser <= maxDist) return searchFilter(alert);
      } else return searchFilter(alert);
    });
  };

  const searchFilter = (alert) => {
    if (!alert) return false;

    const search = params.get('search');
    if (search) {
      return (
        alert.headline
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, '')) ||
        alert.description
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, '')) ||
        alert.sender
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, '')) ||
        alert.type
          ?.toLowerCase()
          .replace(/\s/g, '')
          .includes(search?.toLowerCase().replace(/\s/g, ''))
      );
    } else return true;
  };

  /**
   * Updates the filtered list states.
   * @param {Object} args Expected Object: { page: PageNumber, severitySortValue: SeveritySortDirectionValue, localOnly: ShowLocalOnlyBoolean, maxDistance: MaxDistanceForLocalOnly }
   * @note You only need to input the arguments for the sake of avoiding timing conflicts with useState updates
   */
  function UpdateFilteredLists(args) {
    //this sets the default arguments
    const correctedArgs = {
      page: pageIndex,
      severitySortValue: severitySortDirection,
      localOnly: showLocalOnly,
      maxDistance: maxDist,
      alerts: alertsList,
      ...args,
    };

    let filteredList = FilterAlertList();

    //sort by severity
    if (correctedArgs.severitySortValue !== null)
      filteredList = filteredList.sort((a, b) => {
        if (correctedArgs.severitySortValue) return b.severity - a.severity;
        else return a.severity - b.severity;
      });

    setFilteredAlertList(filteredList);

    let filteredListPaged = GetPageFromList(filteredList, alertLimit, correctedArgs.page);

    //sort by severity
    if (correctedArgs.severitySortValue !== null)
      filteredListPaged = filteredListPaged.sort((a, b) => {
        if (correctedArgs.severitySortValue) return b.severity - a.severity;
        else return a.severity - b.severity;
      });

    setFilteredAlertListPaged(filteredListPaged);
  }

  if (!widget) {
    return (
      <div className="d-flex flex-column h-100">
        <div>
          <div className="d-flex flex-column align-items-center flex-xl-row justify-content-md-between mb-3">
            <div className="d-flex flex-column align-items-center flex-fill flex-xl-row gap-3 gap-xl-5">
              <div className="fs-4 fw-bold">{title}</div>
              <SearchBar className="w-auto d-flex" />

              <div className="d-flex gap-2 align-content-center align-items-center">
                <Toggle
                  value={showLocalOnly}
                  callback={(value) => {
                    setShowLocalOnly(value);
                    setPageIndex(1);
                    UpdateFilteredLists({ localOnly: value });
                  }}
                  label="Show Local Only"
                />
                {showLocalOnly && (
                  <div style={{ marginLeft: 20 }}>
                    Max Distance (mi):{' '}
                    <input
                      type="number"
                      min={0}
                      max={1000}
                      value={maxDist}
                      onChange={(e) => {
                        setMaxDist(e.target.value);
                        UpdateFilteredLists({ maxDistance: e.target.value });
                      }}
                    />
                    <Slider
                      value={maxDist}
                      onChange={(value) => {
                        setPageIndex(1);
                        setMaxDist(value);
                        UpdateFilteredLists({ maxDistance: value });
                      }}
                      min={0}
                      max={1000}
                      step={5}
                      style={{ width: 150 }}
                    />
                  </div>
                )}
              </div>
            </div>
            {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditAlerts']) && (
              <Button
                className="px-5 rounded-3 mt-3"
                onClick={() => navigate('/publicuser/createAlert')}
                variant="primary"
              >
                Create Alert
              </Button>
            )}
          </div>
          {filteredAlertListPaged?.length > 0 && (
            <div
              className="d-flex align-items-center cursor-pointer"
              onClick={() => severitySort()}
            >
              Severity <FaSort />
            </div>
          )}
        </div>
        <div className="overflow-auto">
          {filteredAlertListPaged?.length === 0 && (
            <div className="w-100 text-center">No Results Found...</div>
          )}
          {filteredAlertListPaged?.map((alert, index) => (
            <AlertCard
              key={
                alert.id ||
                alert.ca_identifier ||
                alert.cw_identifier ||
                alert.ga_identifier ||
                index
              }
              index={index}
              alert={alert}
              getAlerts={() => {
                setAlertsList(alertsList.filter((a) => a !== alert));
              }}
              canCloseAlert={parseInt(alert.workplaceId) === parseInt(Cookies.get('workplaceId'))}
              onClose={() => {
                setFilteredAlertListPaged((prev) => prev.filter((item) => item !== alert));
              }}
            />
          ))}
        </div>
        <PageIndex
          pageIndex={pageIndex}
          setPageIndex={(value) => {
            setPageIndex(value);
            UpdateFilteredLists({ page: value });
          }}
          maxPages={filteredAlertList?.length / alertLimit}
        />
      </div>
    );
  }

  if (widget) {
    if (dimensions[0] === 1 && dimensions[1] === 1) {
      return (
        <AlertWidget
          ROUTE={ROUTE}
          filteredAlertListPaged={filteredAlertListPaged}
          dimensions={dimensions}
          maxDist={maxDist}
          alertsList={alertsList}
          setAlertsList={setAlertsList}
          sidebarOpen={sidebarOpen}
          preview={preview}
        />
      );
    }

    if (dimensions[0] === 1 && dimensions[1] === 2) {
      return (
        <AlertWidget
          hasMap={true}
          ROUTE={ROUTE}
          filteredAlertListPaged={filteredAlertListPaged}
          dimensions={dimensions}
          maxDist={maxDist}
          alertsList={alertsList}
          setAlertsList={setAlertsList}
          sidebarOpen={sidebarOpen}
          preview={preview}
        />
      );
    }

    if (dimensions[0] === 2 && dimensions[1] === 1) {
      return (
        <AlertWidget
          ROUTE={ROUTE}
          filteredAlertListPaged={filteredAlertListPaged}
          dimensions={dimensions}
          maxDist={maxDist}
          alertsList={alertsList}
          setAlertsList={setAlertsList}
          sidebarOpen={sidebarOpen}
          preview={preview}
        />
      );
    }
  }
};
